import React, { useEffect, useState } from "react";
import { MdEdit, MdDelete } from "react-icons/md";
import SubCategoryModal from "../../../Components/Modals/Our-Project-Corporate-Design/Sub-Category";
import RecordPosition from "../../../Components/Modals/RecordPosition/RecordPosition";
import { useCorporateDesignProjects } from "../useHook";
import { IoEye } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import GlobalPagination from "../../../Components/Pagination/GlobalPagination";
import {
  FiEdit2,
  FiFilter,
  FiPlus,
  FiRotateCcw,
  FiSearch,
} from "react-icons/fi";
import { ImageWithFallback } from "../../../Utils/loader";
import {
  usePermission,
  SystemPermissions,
} from "../../../Utils/permissionAccess";

const OurProjectCorporateDesignCategories = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const canRead = hasPermission(SystemPermissions.OurProjectCorporateDesignCategories.READ);
  const canCreate = hasPermission(SystemPermissions.OurProjectCorporateDesignCategories.CREATE);
  const canUpdate = hasPermission(SystemPermissions.OurProjectCorporateDesignCategories.UPDATE);
  const canPosition = hasPermission(SystemPermissions.OurProjectCorporateDesignCategories.POSITION);
  const canShowActions = canUpdate || canRead;
  const [currentOurProjectCategory, setCurrentOurProjectCategory] =
    useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPositionModalOpen, setIsPositionModalOpen] = useState(false);
  const [selectedCategoryForPosition, setSelectedCategoryForPosition] =
    useState(null);
  const [ourProjectCategories, setOurProjectCategories] = useState([]);
  const [pagination, setPagination] = useState({
    totalCount: 0,
    totalPages: 0,
    currentPage: 1,
    limit: 10,
    offset: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });
  const [categoryName, setCategoryName] = useState("");
  const [parentCategory, setParentCategory] = useState("finishing");
  const { getSubCategories, updateSubCategoryPosition } = useCorporateDesignProjects();

  const refreshData = async () => {
    const params = {
      offset: pagination.offset,
      limit: pagination.limit,
    };
    if (categoryName) {
      params.category_name = categoryName;
    }
    if (parentCategory) {
      params.parent_category = parentCategory;
    }
    const paginationData = await getSubCategories(
      setOurProjectCategories,
      params,
    );
    if (paginationData) {
      setPagination(paginationData);
    }
  };

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
    if (!isModalOpen) {
      setCurrentOurProjectCategory(null);
    }
  };

  const loadData = async () => {
    const params = {
      offset: pagination.offset,
      limit: pagination.limit,
    };
    if (categoryName) {
      params.category_name = categoryName;
    }
    if (parentCategory) {
      params.parent_category = parentCategory;
    }
    const paginationData = await getSubCategories(
      setOurProjectCategories,
      params,
    );
    if (paginationData) {
      setPagination(paginationData);
    }
  };

  useEffect(() => {
    loadData();
  }, [pagination.offset, pagination.limit]);

  const handlePageChange = (newOffset) => {
    setPagination((prev) => ({
      ...prev,
      offset: newOffset,
    }));
  };

  const handleEditOurProjectCategory = (ourProjectCategory) => {
    setCurrentOurProjectCategory(ourProjectCategory);
    setIsModalOpen(true);
  };

  const handlePageView = (id) => {
    navigate(`/our-project-corporate-design/${id}`);
  };

  const handlePositionClick = (category) => {
    setSelectedCategoryForPosition(category);
    setIsPositionModalOpen(true);
  };

  const handleUpdatePosition = async (id, position) => {
    await updateSubCategoryPosition(id, position);
    await refreshData();
  };

  const closePositionModal = () => {
    setIsPositionModalOpen(false);
    setSelectedCategoryForPosition(null);
  };

  const handleSearch = async () => {
    setPagination((prev) => ({
      ...prev,
      offset: 0,
    }));
    const params = {
      offset: 0,
      limit: pagination.limit,
    };
    if (categoryName) {
      params.category_name = categoryName;
    }
    if (parentCategory) {
      params.parent_category = parentCategory;
    }
    const paginationData = await getSubCategories(
      setOurProjectCategories,
      params,
    );
    if (paginationData) {
      setPagination(paginationData);
    }
  };

  const handleReset = async () => {
    setCategoryName("");
    setParentCategory("");
    setPagination((prev) => ({
      ...prev,
      offset: 0,
    }));
    const paginationData = await getSubCategories(setOurProjectCategories, {
      offset: 0,
      limit: pagination.limit,
    });
    if (paginationData) {
      setPagination(paginationData);
    }
  };

  return (
    <>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Our Project Corporate Design Categories</h2>
        {canCreate && (
          <button
            onClick={() => {
              setCurrentOurProjectCategory(null);
              setIsModalOpen(!isModalOpen);
            }}
            className="btn btn-primary h-[44px]"
          >
            <FiPlus className="text-white" />
            Add Corporate Design Category
          </button>
        )}
      </div>

      <div className="bg-white p-5 w-full rounded-lg pagination-shadow flex flex-wrap xlCustom:flex-nowrap xlCustom:justify-end items-center gap-3 my-5">
        <div className="search-wrapper">
          <input
            type="text"
            className="search-input"
            placeholder="Search by category name"
            value={categoryName}
            onChange={(e) => setCategoryName(e.target.value)}
          />
          <FiSearch className="search-icon" />
        </div>
        <select
          className="status-select"
          value={parentCategory}
          onChange={(e) => setParentCategory(e.target.value)}
        >
          <option value="" className="opacity-25">
            Select Parent Category
          </option>
          <option value="finishing">Finishing Projects</option>
          <option value="grey_structure">Grey Structure Projects</option>
        </select>
        <div className="flex flex-wrap md:flex-nowrap gap-2">
          <button className="btn btn-filter filter-btn" onClick={handleSearch}>
            <FiFilter className="text-white" /> Apply Filter
          </button>
          <button
            className="btn btn-reset-filter filter-btn"
            onClick={handleReset}
          >
            <FiRotateCcw className="text-white" /> Reset Filter
          </button>
        </div>
      </div>

      <div className="bg-white w-full rounded-lg pagination-shadow">
        <div className="overflow-x-auto">
          <table className="min-w-full table-auto bg-white rounded-lg  overflow-hidden">
            <thead>
              <tr className="brand-gradient text-white">
                <th className="table-th">Sr. No</th>
                <th className="table-th">Category Name</th>
                <th className="table-th">Parent Category Name</th>
                <th className="table-th">Image</th>
                {canShowActions && <th className="table-th">Action</th>}
              </tr>
            </thead>
            <tbody>
              {ourProjectCategories.map((ourProjectCategory, index) => (
                <tr
                  key={ourProjectCategory.id}
                  className={`${
                    index % 2 === 0 ? "bg-gray-50" : "bg-gray-100"
                  } hover:bg-gray-200 transition-colors duration-300`}
                >
                  <td className="table-td">
                    {canPosition ? (
                      <button
                        onClick={() => handlePositionClick(ourProjectCategory)}
                        className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                      >
                        {ourProjectCategory.position}
                      </button>
                    ) : (
                      <span>{ourProjectCategory.position}</span>
                    )}
                  </td>
                  <td className="table-td">{ourProjectCategory.name}</td>
                  <td className="table-td">
                    {ourProjectCategory.parentCategory.name}
                  </td>
                  <td className="table-td">
                    <ImageWithFallback
                      src={ourProjectCategory.image_url}
                      alt="img"
                      className="w-16 h-16 object-cover rounded-full shadow-lg"
                    />
                  </td>
                  {canShowActions && (
                    <td className="table-td flex space-x-2 mt-3">
                      {canUpdate && (
                        <button
                          className="btn btn-edit text-white"
                          onClick={() =>
                            handleEditOurProjectCategory(ourProjectCategory)
                          }
                        >
                          <FiEdit2 className="text-white" /> Edit
                        </button>
                      )}

                      {canRead && (
                        <button
                          className="btn btn-view "
                          onClick={() => handlePageView(ourProjectCategory.id)}
                        >
                          <IoEye className="text-white" /> View
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="mt-6">
          <GlobalPagination
            offset={pagination.offset}
            limit={pagination.limit}
            total={pagination.totalCount}
            onPageChange={handlePageChange}
            className=""
          />
        </div>
      )}

      {isModalOpen && (
        <SubCategoryModal
          currentOurProjectCategory={currentOurProjectCategory}
          toggleModal={toggleModal}
          onSuccess={refreshData}
        />
      )}

      {isPositionModalOpen && selectedCategoryForPosition && (
        <RecordPosition
          isOpen={isPositionModalOpen}
          onClose={closePositionModal}
          currentPosition={selectedCategoryForPosition.position}
          recordId={selectedCategoryForPosition.id}
          onUpdatePosition={handleUpdatePosition}
          moduleName="Category"
        />
      )}
    </>
  );
};
export default OurProjectCorporateDesignCategories;
